import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useArticleDateUtils } from "~/components/article/article-author-block/article-date/hooks";
import { usePlusArticlePopup } from "~/components/plus/article-popup/hooks";
import { usePlusDetailedListingPopup } from "~/components/plus/detailed-listing-popup/hooks";
import { PlusPaywallSuspense } from "~/components/plus/plus-paywall-suspense";
import type { dailyPulseBlockQuery$key } from "~/queries/__generated__/dailyPulseBlockQuery.graphql";

import IconArrow from "./icon-arrow.svg";
import {
  Container,
  Content,
  ContentContainer,
  Footer,
  Header,
  Heading,
  LastUpdatedContainer,
  LastUpdatedDate,
  PlaceHolderContainer,
  PreviousEditions,
  ReadFullDailyPulseButton,
  StyledContentHeadline,
  StyledImagePlaceholder,
} from "./style";

export type Props = {
  className?: string;
  reference: dailyPulseBlockQuery$key;
};

export const DailyPulseBlock: FunctionComponent<Props> = ({ className, reference }) => {
  const content = useFragment(
    graphql`
      fragment dailyPulseBlockQuery on Query
      @argumentDefinitions(dailyPulseTypeUuid: { type: "String!" }) {
        dailyPulseQuery: articles(
          filter: { typeIds: [$dailyPulseTypeUuid] }
          first: 1
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              ...contentHeadlineContent
              ...hooksArticleDateUtilsContent
              ...hooksPlusArticleLinkHandlerArticle
              body {
                text
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const topArticle = first(content.dailyPulseQuery.edges)?.node;
  const { lastUpdatedDate } = useArticleDateUtils(topArticle, "h:mma, d MMM yyyy");
  const { handleOpen: handleOpenDetailedListingPopup } = usePlusDetailedListingPopup();
  const { handleOpen: handleOpenArticlePopup } = usePlusArticlePopup();

  if (!topArticle) return null;

  return (
    <Container className={className}>
      <PlusPaywallSuspense
        fallback={
          <PlaceHolderContainer>
            <StyledImagePlaceholder />
          </PlaceHolderContainer>
        }
      >
        <Header>
          <Heading>DAILY PULSE</Heading>
          <StyledContentHeadline reference={topArticle} preferSocialHeadline />
          <LastUpdatedContainer>
            Last updated: <LastUpdatedDate>{lastUpdatedDate}</LastUpdatedDate>
          </LastUpdatedContainer>
        </Header>
        <ContentContainer>
          <Content>{topArticle.body?.text?.replace(/(.\n)(?!\n)/g, ".\n\n") ?? ""}</Content>
          <Footer>
            <ReadFullDailyPulseButton noStyle onClick={() => handleOpenArticlePopup(topArticle)}>
              READ FULL DAILY PULSE
            </ReadFullDailyPulseButton>
            <PreviousEditions
              onClick={() =>
                handleOpenDetailedListingPopup({
                  pgtype: "homepage",
                  type: "daily-pulse",
                })
              }
            >
              <IconArrow />
              Previous editions
            </PreviousEditions>
          </Footer>
        </ContentContainer>
      </PlusPaywallSuspense>
    </Container>
  );
};

DailyPulseBlock.displayName = "DailyPulseBlock";
